/**
 * VerdictView, the v3 closed-case screen.
 *
 * Renders when a bill reaches status = 'completed' or 'no_savings'.
 * Branches between two outcomes so the page never reads "$0 saved":
 *
 *  1. Win  — savedTotal > 0
 *     Confident hero ("Verdict's in. You saved: $X"), line-item breakdown,
 *     concise settlement card with Mark as paid.
 *
 *  2. No-savings — savedTotal === 0 or status === 'no_savings'
 *     Honest hero ("Verdict's in. We couldn't beat this one"), explainer
 *     of what was checked, encouragement to keep using Mediloop. No
 *     settlement card, no "Download letter".
 */

import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { phVerdictViewed, phBillMarkedPaid, phFailure } from '../lib/posthog';
import type { Bill } from '../types/bill';
import '@styles/components/verdict-view.css';

type Settlement = {
  id: string;
  description: string;
  rationale: string | null;
  was_amount: number;
  now_amount: number;
  saved_amount: number;
  sort_order: number;
};

type Props = {
  bill: Bill;
};

export default function VerdictView({ bill }: Props) {
  const [settlements, setSettlements] = useState<Settlement[] | null>(null);
  const [loading, setLoading] = useState(true);
  /* paid_at lives on bills (migration 047). We mirror it in local state
     so the button can toggle without forcing a parent re-fetch. */
  const [paidAt, setPaidAt] = useState<string | null>(
    (bill as { paid_at?: string | null }).paid_at ?? null,
  );
  const [markingPaid, setMarkingPaid] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('bill_settlements')
        .select(
          'id, description, rationale, was_amount, now_amount, saved_amount, sort_order',
        )
        .eq('bill_id', bill.id)
        .order('sort_order', { ascending: true });
      if (!cancelled) {
        setSettlements(data ?? []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bill.id]);

  /* Self-report toggle. Goes through mark_bill_paid RPC (migration 048)
     because RLS blocks customers from UPDATE-ing bills directly. The
     RPC owns the ownership check + returns the new paid_at server-side,
     so we trust its return value over the optimistic local guess. */
  const handleMarkAsPaid = async () => {
    if (markingPaid) return;
    const wantPaid = !paidAt;
    const previousPaidAt = paidAt;
    /* Optimistic flip — use a placeholder ISO so the button reads as
       "Marked paid" immediately. Real server timestamp arrives below. */
    setMarkingPaid(true);
    setPaidAt(wantPaid ? new Date().toISOString() : null);

    const { data, error } = await supabase.rpc('mark_bill_paid', {
      bill_id: bill.id,
      paid: wantPaid,
    });

    if (error) {
      console.error('Failed to toggle paid_at via mark_bill_paid', error);
      phFailure({
        step: 'mark_bill_paid',
        message: error.message,
        context: { bill_id: bill.id },
      });
      setPaidAt(previousPaidAt); // revert
    } else {
      /* RPC returns the new paid_at (timestamptz or null). */
      setPaidAt((data as string | null) ?? null);
      /* Self-reported payment is the closest thing we have to a "case
         genuinely resolved" signal from the customer's side, and it was
         untracked. Useful as a retention/satisfaction proxy. */
      phBillMarkedPaid({ billId: bill.id, paid: wantPaid, savedTotal });
    }
    setMarkingPaid(false);
  };

  /* Prefer the structured saved amount (sum of settlements) if available,
     otherwise fall back to the legacy bills.amount_saved field which the
     admin app has been writing for years. This makes the new screen work
     for closed cases that pre-date the bill_settlements table. */
  const savedTotal =
    settlements && settlements.length > 0
      ? settlements.reduce((acc, s) => acc + Number(s.saved_amount), 0)
      : Number(bill.amount_saved) || 0;

  const wasTotal =
    settlements && settlements.length > 0
      ? settlements.reduce((acc, s) => acc + Number(s.was_amount), 0)
      : Number(bill.bill_amount) || 0;

  const nowTotal = Math.max(wasTotal - savedTotal, 0);
  const percentSaved = wasTotal > 0 ? Math.round((savedTotal / wasTotal) * 100) : 0;

  /* The branch — drives both hero copy and settlement card. */
  const noSavings = savedTotal <= 0 || bill.status === 'no_savings';

  /* ⚠️ 2026-08-08 — THE SAVINGS REVEAL, previously untracked.
     This component is the moment the customer finds out what mediloop got
     them, and it emitted nothing. There was no way to answer "do users who
     see a positive verdict retain and upgrade more than users who see
     no_savings" — the central retention question for this product — because
     the outcome side of the funnel simply did not exist in analytics.

     Fires once per bill per mount, and only after the settlements query has
     resolved (`loading === false`), otherwise savedTotal/wasTotal would be
     read off the pre-fetch fallback and report the wrong numbers. */
  const verdictTracked = useRef<string | null>(null);
  useEffect(() => {
    if (loading) return;
    if (verdictTracked.current === bill.id) return;
    verdictTracked.current = bill.id;

    phVerdictViewed({
      billId: bill.id,
      wasTotal,
      savedTotal,
      percentSaved,
      noSavings,
      status: bill.status,
    });
  }, [loading, bill.id, bill.status, wasTotal, savedTotal, percentSaved, noSavings]);

  const fmt = (n: number) =>
    n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div className="verdict-view">
      {/* ─── Hero ─── */}
      {noSavings ? (
        <div className="verdict-hero verdict-hero--neutral">
          <div className="verdict-eyebrow">  Case closed  </div>
          <div className="verdict-headline-lg">
            We couldn't <span className="italic">beat this one.</span>
          </div>
          <p className="verdict-hero-blurb">
            Agent Loop audited every line on this {wasTotal > 0 ? (
              <>
                <span className="num">${fmt(wasTotal)}</span>{' '}
              </>
            ) : null}
            bill and the provider's charges held up under scrutiny.
            No errors, no overcharges, no negotiation leverage.
          </p>
        </div>
      ) : (
        <div className="verdict-hero">
          <div className="verdict-eyebrow">  Case closed  </div>
          <div className="verdict-headline">
            Verdict's in. <span className="italic">You saved:</span>
          </div>
          <div className="verdict-amount num">
            <span className="verdict-amount-pre">$</span>
            {fmt(savedTotal).split('.')[0]}
            <span className="verdict-amount-cents">.{fmt(savedTotal).split('.')[1]}</span>
          </div>
          {wasTotal > 0 && (
            <div className="verdict-subhead">
              on a <span className="num">${fmt(wasTotal)}</span> bill ·{' '}
              <span className="italic">a {percentSaved}% reduction.</span>
            </div>
          )}
        </div>
      )}

      {/* ─── Line-item breakdown (only for the win path with structured data) ─── */}
      {!noSavings && loading ? (
        <div className="verdict-skeleton" aria-busy="true">
          <div className="verdict-skeleton-row" />
          <div className="verdict-skeleton-row" style={{ width: '78%' }} />
          <div className="verdict-skeleton-row" style={{ width: '62%' }} />
        </div>
      ) : !noSavings && settlements && settlements.length > 0 ? (
        <div className="verdict-breakdown">
          <div className="verdict-breakdown-label">  The breakdown  </div>
          {settlements.map((s) => (
            <div key={s.id} className="verdict-line">
              <div className="verdict-line-desc">
                <div>{s.description}</div>
                {s.rationale && (
                  <div className="verdict-line-rationale">{s.rationale}</div>
                )}
              </div>
              <div className="verdict-line-was num">${fmt(s.was_amount)}</div>
              <div className="verdict-line-saved num">− ${fmt(s.saved_amount)}</div>
            </div>
          ))}
          <div className="verdict-line verdict-line-total">
            <div className="verdict-line-desc">
              <div>New balance with provider</div>
            </div>
            <div className="verdict-line-was num">${fmt(wasTotal)}</div>
            <div className="verdict-line-saved num">${fmt(nowTotal)}</div>
          </div>
        </div>
      ) : null}

      {/* ─── Bottom action card ─── */}
      {noSavings ? (
        <div className="verdict-footer">
          <div className="verdict-footer-body">
            <div className="verdict-footer-line">
              <span className="italic">What you owe.</span>{' '}
              The provider's original balance{' '}
              {wasTotal > 0 ? (
                <>of <span className="num">${fmt(wasTotal)}</span></>
              ) : null}{' '}
              still stands. Pay through their portal whenever you're ready.
            </div>
          </div>
          <button
            className={`verdict-btn ${paidAt ? 'verdict-btn-paid' : 'verdict-btn-ghost'}`}
            onClick={handleMarkAsPaid}
            disabled={markingPaid}
          >
            {paidAt ? (
              <>
                <Check size={14} aria-hidden /> Marked paid
              </>
            ) : markingPaid ? 'Saving…' : 'Mark as paid'}
          </button>
        </div>
      ) : (
        <div className="verdict-footer">
          <div className="verdict-footer-body">
            <div className="verdict-footer-line">
              <span className="italic">New balance.</span>{' '}
              You owe the provider{' '}
              <span className="num">${fmt(nowTotal)}</span>. Settle it through
              their patient portal whenever you're ready.
            </div>
          </div>
          <button
            className={`verdict-btn ${paidAt ? 'verdict-btn-paid' : 'verdict-btn-ghost'}`}
            onClick={handleMarkAsPaid}
            disabled={markingPaid}
          >
            {paidAt ? (
              <>
                <Check size={14} aria-hidden /> Marked paid
              </>
            ) : markingPaid ? 'Saving…' : 'Mark as paid'}
          </button>
        </div>
      )}
    </div>
  );
}
